<template>
    <KTooltip
        :title="tooltip"
        :open-delay="openDelay"
        :activatorType="`#${computedId}`"
        :max-width="tooltipMaxWidth"
        :text-wrap="tooltipTextWrap"
        :text-transform="tooltipTextTransform"
        :white-space="tooltipWhiteSpace"
    >
        <template #activator="activator">
            <v-btn
                elevation="0"
                :id="computedId"
                ref="buttonRef"
                :size="computedSize"
                :rounded="rounded"
                :style="{
                    borderRadius: rounded === true ? '5px' : undefined,
                }"
                class="k-icon-button"
                :class="{
                    'no-hover': noHover,
                    [activeClass]: isVisuallyActive,
                    'keep-hover-opacity': keepHoverOpacity,
                }"
                :active="isVisuallyActive"
                :disabled="disabled"
                minWidth="0"
                v-bind="{ ...$attrs, ...activator.props }"
                @mouseleave="emit('mouseleave-tooltip')"
            >
                <template v-if="customLoader" #loader>
                    <slot name="custom-loader" />
                </template>

                <slot :iconColorStyle="iconColorStyle">
                    <v-icon
                        :icon="computedIcon"
                        :size="computedIconSize"
                        :class="[
                            { active: isVisuallyActive },
                            iconClass,
                            iconColorClass,
                        ]"
                        :color="iconColor"
                        :style="iconColorStyle"
                    />
                    <slot name="append" />
                </slot>
            </v-btn>
        </template>
    </KTooltip>
</template>

<script setup>
import { computed, getCurrentInstance, h, useAttrs, useSlots } from "vue";
import MessageCircleMore from "Admin/js/Components/Icons/MessageCircleMore.vue";

defineOptions({
    inheritAttrs: false,
});

const CHAT_ICON_ALIASES = new Set([
    "chat",
    "sym_r_chat",
    "sym_o_chat",
    "sym_s_chat",
    "mdi-message-text-outline",
    "mdi-message-text",
    "mdi-message-reply-text-outline",
]);

function isChatIcon(icon) {
    return icon === MessageCircleMore || CHAT_ICON_ALIASES.has(icon);
}

const emit = defineEmits(["mouseleave-tooltip"]);

const props = defineProps({
    id: String,
    rounded: {
        type: [String, Boolean],
        default: true,
    },
    icon: [String, Function, Object, Array],
    size: {
        type: [String, Number],
        default: 25,
    },
    iconSize: {
        type: [String, Number],
        default: 18,
    },
    iconClass: {
        type: String,
        default: "text-fontLight",
    },
    iconColor: {
        type: String,
        default: "icon",
    },
    iconHoverColor: {
        type: String,
        default: "fontLight",
    },
    iconActiveColor: {
        type: String,
        default: "fontLight",
    },
    active: {
        type: Boolean,
        default: false,
    },
    activeClass: {
        type: String,
        default: "bg-secondary text-fontLight",
    },
    tooltip: String,
    tooltipMaxWidth: {
        type: String,
        default: null,
    },
    tooltipTextWrap: {
        type: String,
        default: "nowrap",
    },
    tooltipTextTransform: {
        type: String,
        default: "uppercase",
    },
    tooltipWhiteSpace: {
        type: String,
        default: "normal",
    },
    openDelay: {
        type: Number,
        default: 500,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    noHover: {
        type: Boolean,
        default: false,
    },
    customLoader: {
        type: Boolean,
        default: false,
    },
    keepHoverOpacity: {
        type: Boolean,
        default: false,
    },
});

const instance = getCurrentInstance();
const uid = `k-icon-button__${instance.uid}`;
const attrs = useAttrs();

const computedId = computed(() => props.id || uid);

/** Selected styling must not apply while disabled (e.g. chin lock). */
const isVisuallyActive = computed(() => props.active && !props.disabled);

const computedSize = computed(() => {
    if (attrs.width || attrs.height) {
        return "default";
    }
    return props.size;
});

const computedIconSize = computed(() => {
    if (!props.iconSize) return;

    if (typeof props.iconSize === "number") {
        return `${props.iconSize}px`;
    }
    return props.iconSize;
});

const materialVariants = ["outlined", "rounded", "sharp"];
const materialSymbolSyntax = "sym_";
function getMaterialSymbolAndClass() {
    const material = {
        class: "material-symbols",
        symbol: props.icon.substring(materialSymbolSyntax.length),
    };

    let found = false;
    for (const variant of materialVariants) {
        const syntax = variant[0] + "_";
        if (material.symbol.startsWith(syntax)) {
            found = true;
            material.symbol = material.symbol.substring(syntax.length);
            material.class = material.class + `-${variant}`;
            break;
        }
    }

    if (!found) {
        material.class = material.class + `-${materialVariants[0]}`;
    }

    return material;
}

function isMaterialSymbol() {
    return (
        typeof props.icon === "string" &&
        props.icon.startsWith(materialSymbolSyntax)
    );
}

const computedIcon = computed(() => {
    if (isChatIcon(props.icon)) {
        return () =>
            h(MessageCircleMore, {
                size: computedIconSize.value,
                width: computedIconSize.value,
                height: computedIconSize.value,
            });
    }

    if (typeof props.icon === "object") {
        return () =>
            h(props.icon, {
                width: computedIconSize.value,
                height: computedIconSize.value,
            });
    }

    if (isMaterialSymbol()) {
        const material = getMaterialSymbolAndClass();
        return () =>
            h(
                "span",
                {
                    class: [material.class],
                    style: {
                        fontSize: computedIconSize.value,
                    },
                },
                material.symbol,
            );
    }

    return props.icon;
});

// Dynamic icon color classes
const iconColorClass = computed(() => {
    // Check if it's a manual color (hex, rgb, etc.) or theme color
    if (
        props.iconColor.startsWith("#") ||
        props.iconColor.startsWith("rgb") ||
        props.iconColor.startsWith("rgba")
    ) {
        return ""; // Manual color, no class needed
    }
    return `text-${props.iconColor}`;
});

// Computed styles for manual colors
const iconColorStyle = computed(() => {
    const styles = {};

    // Default color
    if (
        props.iconColor.startsWith("#") ||
        props.iconColor.startsWith("rgb") ||
        props.iconColor.startsWith("rgba")
    ) {
        styles.color = props.iconColor;
    }

    // Hover color
    if (
        props.iconHoverColor.startsWith("#") ||
        props.iconHoverColor.startsWith("rgb") ||
        props.iconHoverColor.startsWith("rgba")
    ) {
        styles["--icon-hover-color"] = props.iconHoverColor;
    } else {
        styles["--icon-hover-color"] =
            `rgb(var(--v-theme-${props.iconHoverColor}))`;
    }

    // Active color
    if (
        props.iconActiveColor.startsWith("#") ||
        props.iconActiveColor.startsWith("rgb") ||
        props.iconActiveColor.startsWith("rgba")
    ) {
        styles["--icon-active-color"] = props.iconActiveColor;
    } else {
        styles["--icon-active-color"] =
            `rgb(var(--v-theme-${props.iconActiveColor}))`;
    }

    return styles;
});
</script>

<style lang="scss">
@import "../../../../sass/_theme-variables.sass";

.k-icon-button.v-btn {
    color: $fontLight;
    background-color: transparent;

    &.v-btn--disabled {
        .v-btn__overlay {
            opacity: 0;
        }
    }

    &:not(.v-btn--disabled):not(.cursor-not-allowed):not(.v-btn--active):hover {
        color: var(--icon-hover-color) !important;
        .v-btn__content {
            .v-icon,
            .prospus-icon {
                color: var(--icon-hover-color) !important;
            }
        }
    }

    &.v-btn--active {
        .v-btn__overlay {
            opacity: 0;
        }

        .v-btn__content {
            .v-icon,
            .prospus-icon {
                color: var(--icon-active-color) !important;
            }
        }

        &:not(.v-btn--disabled):hover {
            .v-btn__overlay {
                opacity: 0;
            }
        }
    }

    &.keep-hover-opacity {
        &.v-btn--active {
            &:not(.v-btn--disabled):hover {
                .v-btn__overlay {
                    opacity: calc(
                        var(--v-hover-opacity) *
                            var(--v-theme-overlay-multiplier)
                    ) !important;
                }
            }
        }
    }

    &.v-btn--disabled {
        cursor: not-allowed;
        pointer-events: auto;
        opacity: 0.5 !important;
        background-color: transparent !important;

        .v-btn__overlay {
            opacity: 0 !important;
        }
    }

    &.no-hover {
        &:hover {
            .v-btn__overlay {
                opacity: 0 !important;
            }
        }
    }

    &.cursor-not-allowed {
        .v-btn__overlay {
            opacity: 0;
        }
    }
    .material-symbols-rounded {
        font-size: inherit;
    }
}
</style>
